import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications";
import { createFilterQuery } from "@via/api/user-groups-helper.ts";
import { ErrorNotification } from "@via/utils/interfaces";
import { useImpersonationUser } from "@via/hooks/useImpersonationUser.tsx";

export const fetchUserGroups = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: Array<{ id: string; value: unknown }>,
  userId?: number
) => {
  try {
    const baseUrl = "/api/groups/v1";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });
    if (sort && sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    if (filter && filter.length > 0) {
      const queryFilter = createFilterQuery(filter);
      queryParams.append("filter", queryFilter);
    }
    if (userId) {
      queryParams.append("userId", userId.toString());
    }
    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useUserGroups = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filters: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true
) => {
  const skip = page * pageSize;
  const take = pageSize;
  const impersonationId = useImpersonationUser();

  return useQuery({
    queryKey: ["/api/groups/v1", take, skip, sort, filters, impersonationId],
    queryFn: () => fetchUserGroups(take, skip, sort, filters, impersonationId),
    retry: false,
    enabled,
  });
};

export const useCreateUserGroup = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  const impersonationId = useImpersonationUser();

  return useMutation({
    mutationFn: async (payload: { name: string }) => {
      const res = await axios.post("/api/groups/v1", payload, {
        params: {
          ...(impersonationId && { userId: impersonationId }),
        },
      });
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "user-group-created",
        variant: NOTIFICATION.SUCCESS,
        message: "User group created successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/groups/v1"] });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-group-creation-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to create user group",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const useUpdateUserGroupName = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  const impersonationId = useImpersonationUser();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const res = await axios.patch(
        `/api/groups/v1/${id}`,
        { name },
        {
          params: {
            ...(impersonationId && { userId: impersonationId }),
          },
        }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups/v1"] });
      setNotification({
        id: "user-group-name-updated-successfully",
        variant: NOTIFICATION.SUCCESS,
        message: "Group name updated successfully",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-group-name-update-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to update group name",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const useDeleteUserGroup = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete(`/api/groups/v1/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/groups/v1"] });
      setNotification({
        id: "user-group-deleted-successfully",
        variant: NOTIFICATION.SUCCESS,
        message: "User group deleted successfully",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-group-deletion-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to delete user group",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const fetchUserGroupMembers = async ({
  groupId,
  take,
  skip,
  sort,
  filter,
  includePending,
}: {
  groupId: number | undefined;
  take: number;
  skip: number;
  sort?: Array<{ id: string; desc: boolean }>;
  filter?: Array<{ id: string; value: unknown }>;
  includePending?: boolean;
}) => {
  try {
    const baseUrl = `/api/user/v1/user-group/${groupId}`;
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });
    if (sort && sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    if (filter && filter.length > 0) {
      const queryFilter = createFilterQuery(filter);
      queryParams.append("filter", queryFilter);
    }
    if (includePending) {
      queryParams.append("includePending", "true");
    }
    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useUserGroupMembers = ({
  groupId,
  page = 0,
  pageSize = 10,
  sort = [],
  filter = [],
  includePending = false,
  enabled = true,
}: {
  groupId: number | undefined;
  page?: number;
  pageSize?: number;
  sort?: Array<{ id: string; desc: boolean }>;
  filter?: Array<{ id: string; value: unknown }>;
  includePending?: boolean;
  enabled?: boolean;
}) => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: [
      "/api/user/v1/user-group",
      groupId,
      take,
      skip,
      sort,
      filter,
      includePending,
    ],
    queryFn: () =>
      fetchUserGroupMembers({
        groupId,
        take,
        skip,
        sort,
        filter,
        includePending,
      }),
    retry: false,
    enabled,
  });
};

export const useAddUserGroupMember = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: { groupId: number; email: string }) => {
      const res = await axios.post(`/api/user/v1/user-group`, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "user-added-to-group-successfully",
        variant: NOTIFICATION.SUCCESS,
        message: "User added to group successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/v1/user-group"] });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-group-member-addition-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to add user to a group",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const useDeleteUserGroupMember = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: { groupId: string; userId: string }) => {
      const res = await axios.delete(
        `/api/user/v1/user-group/${payload.groupId}`,
        {
          data: { userId: payload.userId },
        }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/v1/user-group"] });
      queryClient.invalidateQueries({ queryKey: ["/api/groups/v1"] });
      setNotification({
        id: "user-group-member-deleted-successfully",
        variant: NOTIFICATION.SUCCESS,
        message: "User deleted successfully",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-group-member-deletion-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to delete user",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};
